import RequestFormField from "./field"
import {BackGround} from "../../../gd-app/panels/preferences-panel/settings-background/settings-background"
import ModelProxy from "../../model-proxy"
import {convertFromOldFormat} from "../functions/convert-from-old-format"
import {SharedViewLogoType} from "../../../modules/shared-views/types"
import RequestFormCrmIntegration from "./crm-integration"
import {RequestForm_FieldTreatAs, RequestFormType} from "../../../../app/tools/request-forms2/const/const"
import {FieldType} from "../../../fields3/field-type"
import getMandatoryFormFields from "../../../../app/tools/request-forms2/functions/get-mandatory-form-fields"
import {MandatoryFieldsIds} from "../../../../app/tools/request-forms2/@const"
import {autoSetTreatAsFroOldFields} from "../functions/auto-set-treat-as-for-old-fields";

// const debug = "RequestFormSettings"
const debug = false

export default class RequestFormSettings {

    version                 : number = 2

    fields                  : RequestFormField[]

    color                   : number                // form header and background color (REQUIRED)
    background              : BackGround // default... BackGround.GRAY
    logo                    : SharedViewLogoType

    headerTitle             : string        // form header
    headerDescription       : string        // description in the header for the user filling out the form
    confirmationTitle?      : string        // title shown on successful form submission
    confirmationMessage?    : string        // message shown on successful form submission under title

    updatedFields           : Set<string>

    crmIntegration          : RequestFormCrmIntegration

    constructor(requestFormType: RequestFormType, data: any) {

        if (data.header) {
            data = convertFromOldFormat(data)
        }

        const fields = _.map(_.get(data, 'fields', []), formFieldConfig=>{
            return new RequestFormField(formFieldConfig)
        })

        debug && console.log(debug, 'constructor', data.version, data)

        switch (data.version) {
            case 2:
                this.fields = fields
                break

            case 1:
            default:
                const mandatoryFormFields = getMandatoryFormFields(requestFormType)
                const presentMandatoryFields = _.filter(fields, requestFormField=>requestFormField.isMandatory)
                let missingMandatoryFields = _.differenceBy(mandatoryFormFields, presentMandatoryFields, requestFormField=>requestFormField.field?.fieldId)

                // make all fields except MandatoryFieldsIds.TITLE not mandatory
                _.map(fields, requestFormField=>{
                    if (requestFormField.taskFieldId !== MandatoryFieldsIds.TITLE) {
                        requestFormField.isMandatory = false
                    }
                })

                this.fields = _.union(missingMandatoryFields, fields)

                autoSetTreatAsFroOldFields(this.fields)
                break
        }

        debug && console.log(debug, 'constructor fields', this.fields)

        this.color                  = _.get(data, 'color', 11)
        this.background             = _.get(data, 'background', BackGround.GRAY)
        this.logo                   = _.get(data, 'logo', SharedViewLogoType.GOODDAY)

        this.headerTitle            = _.get(data, 'headerTitle', null)
        this.headerDescription      = _.get(data, 'headerDescription', null)
        this.confirmationTitle      = _.get(data, 'confirmationTitle', 'Your request has been successfully sent.')
        this.confirmationMessage    = _.get(data, 'confirmationMessage', undefined)

        this.crmIntegration         = new RequestFormCrmIntegration(_.get(data, 'crmIntegration', null))

        this.updatedFields          = new Set()

        return new Proxy(this, ModelProxy)
    }

    getExistingTaskFieldsIds = () => {
        return _.compact(_.map(this.fields, formField=>formField.taskFieldId))
    }

    addField(formField: RequestFormField) {
        this.fields.push(formField)

        // auto set treat as to new field
        switch (formField.field?.type) {
            case FieldType.EMAIL:
                this.setTreatAs(formField, RequestForm_FieldTreatAs.USER_EMAIL)
                break

            case FieldType.PHONE:
                this.setTreatAs(formField, RequestForm_FieldTreatAs.USER_PHONE)
                break
        }

        this.updatedFields.add('fields')
    }

    updateField(formField: RequestFormField, data:any) {
        formField.applyDelta(data)
        this.setTreatAs(formField, data?.treatAs)
        this.updatedFields.add('fields')
    }

    removeField(formField: RequestFormField) {
        _.remove(this.fields, formField)
        this.updatedFields.add('fields')
    }

    private setTreatAs(formField: RequestFormField, treatAs?: RequestForm_FieldTreatAs) {
        //setting through assign methods ensures that no fields with same treatAs value are present
        switch (treatAs) {
            case RequestForm_FieldTreatAs.USER_NAME:
                this.nameField = formField
                break

            case RequestForm_FieldTreatAs.USER_PHONE:
                this.phoneField = formField
                break

            case RequestForm_FieldTreatAs.USER_EMAIL:
                this.emailField = formField
                break

            default:
                formField.treatAs = undefined
        }
    }

    get nameField(): RequestFormField | undefined {
        let field = _.find(this.fields, (formField: RequestFormField) => {
            return formField.treatAs === RequestForm_FieldTreatAs.USER_NAME
        })

        // if field not found then try to find old format name field
        if (!field) {
            field = _.find(this.fields, (formField: RequestFormField) => {
                return formField.formFieldConfig?.fieldId === MandatoryFieldsIds.NAME
            })
        }
        return field
    }

    set nameField(formField: RequestFormField | undefined) {
        _.map(this.fields, (formField: RequestFormField) => {
            if (formField.treatAs === RequestForm_FieldTreatAs.USER_NAME) {
                formField.treatAs = undefined
            }
        })

        if (formField) {
            formField.treatAs = RequestForm_FieldTreatAs.USER_NAME
        }
    }

    get emailField(): RequestFormField | undefined {
        let field = _.find(this.fields, (formField: RequestFormField) => {
            return formField.treatAs === RequestForm_FieldTreatAs.USER_EMAIL
        })

        // if field not found then try to find old format email field
        if (!field) {
            field = _.find(this.fields, (formField: RequestFormField) => {
                return formField.formFieldConfig?.fieldId === MandatoryFieldsIds.EMAIL
            })
        }
        return field
    }

    set emailField(formField: RequestFormField | undefined) {
        if (formField?.field?.type !== FieldType.EMAIL) return

        _.map(this.fields, (formField: RequestFormField) => {
            if (formField.treatAs === RequestForm_FieldTreatAs.USER_EMAIL) {
                formField.treatAs = undefined
            }
        })

        formField.treatAs = RequestForm_FieldTreatAs.USER_EMAIL
    }

    get phoneField(): RequestFormField | undefined {
        return _.find(this.fields, (formField: RequestFormField) => {
            return formField.treatAs === RequestForm_FieldTreatAs.USER_PHONE
        })
    }

    set phoneField(formField: RequestFormField | undefined) {
        if (formField?.field?.type !== FieldType.PHONE) return

        _.map(this.fields, (formField: RequestFormField) => {
            if (formField.treatAs === RequestForm_FieldTreatAs.USER_PHONE) {
                formField.treatAs = undefined
            }
        })

        formField.treatAs = RequestForm_FieldTreatAs.USER_PHONE
    }

    toJSON(): JSONObject {
        return {
            version             : this.version,
            fields              : _.map(this.fields, formField=>formField.toJSON()),
            color               : this.color,
            background          : this.background,
            logo                : this.logo,
            headerTitle         : this.headerTitle,
            headerDescription   : this.headerDescription,
            confirmationTitle   : this.confirmationTitle,
            confirmationMessage : this.confirmationMessage,
            crmIntegration      : this.crmIntegration.toJSON()
        }
    }
}